import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCountUp } from "@/lib/useCountUp";
import { useCardTilt } from "@/lib/useCardTilt";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, ArrowRight, AlertTriangle, TrendingUp, Zap,
  Check, CalendarCheck, Download,
} from "lucide-react";

// ---------- Audit Risk Meter (SVG speedometer) ----------
export function AuditRiskMeter({ stats, receipts, user }) {
  const [leadState, setLeadState] = useState("idle"); // idle | submitting | booked
  const [delta, setDelta] = useState(null); // integer delta vs last recorded week
  const score = (() => {
    if (!stats) return 0;
    let s = 0;
    // Deductible ratio too high → risk
    const ded = Number(stats.deductible || 0);
    const tot = Number(stats.total_spent || 0);
    const dedRatio = tot > 0 ? ded / tot : 0;
    if (dedRatio > 0.8) s += 35;
    else if (dedRatio > 0.5) s += 15;
    // Category concentration
    const cats = Object.values(stats.by_category || {});
    const catTotal = cats.reduce((a, b) => a + b, 0);
    const topCatShare = catTotal > 0 ? Math.max(...cats) / catTotal : 0;
    if (topCatShare > 0.7) s += 25;
    else if (topCatShare > 0.5) s += 10;
    // Round-number bias
    const rounds = (receipts || []).filter((r) => Number(r.total || 0) % 100 === 0 && Number(r.total || 0) > 0).length;
    if ((receipts || []).length > 0) {
      const roundRatio = rounds / receipts.length;
      if (roundRatio > 0.3) s += 20;
      else if (roundRatio > 0.15) s += 10;
    }
    // Low volume
    if ((stats.receipt_count || 0) < 5) s += 10;
    return Math.min(100, s);
  })();

  const label = score < 30 ? "Safe" : score < 65 ? "Moderate" : "High";
  const strokeColor = score < 30 ? "#10B981" : score < 65 ? "#F59E0B" : "#EF4444";

  // ---- Risk Exposure Log — persistent audit-critical timeline ----
  // Every rising-edge transition INTO the "high" tier appends an immutable,
  // timestamped telemetry record itemizing the breach metrics so operators
  // retain an unalterable operational history for compliance review.
  const [riskLog, setRiskLog] = useState(() => {
    try {
      const raw = window.localStorage.getItem("rs.audit.exposureLog");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(-25) : [];
    } catch (_) { return []; }
  });

  // Recompute breach factors (matches the same math as `score` above)
  const breachFactors = (() => {
    const ded = Number(stats?.deductible || 0);
    const tot = Number(stats?.total_spent || 0);
    const dedRatio = tot > 0 ? ded / tot : 0;
    const cats = Object.values(stats?.by_category || {});
    const catTotal = cats.reduce((a, b) => a + b, 0);
    const topCatShare = catTotal > 0 ? Math.max(...cats) / catTotal : 0;
    const rlist = receipts || [];
    const rounds = rlist.filter((r) => Number(r.total || 0) % 100 === 0 && Number(r.total || 0) > 0).length;
    const roundRatio = rlist.length > 0 ? rounds / rlist.length : 0;
    return { dedRatio, topCatShare, roundRatio, receiptCount: rlist.length };
  })();

  // Broadcast the current risk tier so the AuroraRibbon (and any other
  // ambient surface) can adaptively swap its palette in real time.
  // On transition INTO the critical/"high" tier we also dispatch a
  // persistent (non-auto-dismissing) system-wide warning toast so the
  // user is nudged to review exposure thresholds before conflict settles.
  const lastTierRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const tier = score < 30 ? "safe" : score < 65 ? "moderate" : "high";
    window.__rsRiskTier = tier;
    window.dispatchEvent(new CustomEvent("audit-risk-tier", { detail: { tier, score } }));
    if (tier === "high" && lastTierRef.current && lastTierRef.current !== "high") {
      toast.error(
        "Audit-risk exposure has entered CRITICAL. Review deductible ratios, category concentration, and round-number bias immediately.",
        {
          id: "audit-risk-critical",
          duration: Infinity,
          description: `Composite risk index ${score}/100 — dismiss only after remediation.`,
        }
      );
      // Append a permanent, timestamped exposure record.
      const entry = {
        ts: new Date().toISOString(),
        score,
        label: "High",
        breach: {
          deductible_ratio: Number(breachFactors.dedRatio.toFixed(3)),
          top_category_share: Number(breachFactors.topCatShare.toFixed(3)),
          round_number_ratio: Number(breachFactors.roundRatio.toFixed(3)),
          receipt_count: breachFactors.receiptCount,
        },
      };
      setRiskLog((prev) => {
        const next = [...prev, entry].slice(-25);
        try { window.localStorage.setItem("rs.audit.exposureLog", JSON.stringify(next)); } catch (_) { /* silent */ }
        return next;
      });
    }
    if (tier !== "high") toast.dismiss("audit-risk-critical");
    lastTierRef.current = tier;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score]);

  // ---- Risk Delta Ticker ----
  // Persist a rolling snapshot of {isoWeek, score} in localStorage.
  // The chip surfaces the score delta between the CURRENT week and the most
  // recent previously-recorded week (so the ticker only refreshes weekly).
  useEffect(() => {
    if (!stats) return;
    const now = new Date();
    // ISO-year + ISO-week key so a fresh Monday rolls the comparison forward
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    const weekKey = `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;

    const storageKey = "rs.audit.weekly";
    let history = [];
    try {
      const raw = window.localStorage.getItem(storageKey);
      history = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(history)) history = [];
    } catch (_) { history = []; }

    const priorEntries = history.filter((h) => h.week !== weekKey);
    // Only compare against a snapshot from within the last 4 weeks so a stale
    // 2023-W40 entry can't produce a misleading delta after a long idle period.
    const priorScore = (() => {
      if (!priorEntries.length) return null;
      const last = priorEntries[priorEntries.length - 1];
      const lastTs = new Date(last.ts || 0).getTime();
      const FOUR_WEEKS_MS = 28 * 24 * 60 * 60 * 1000;
      if (!lastTs || Date.now() - lastTs > FOUR_WEEKS_MS) return null;
      return Number(last.score);
    })();
    if (priorScore !== null) setDelta(Math.round(score - priorScore));
    else setDelta(null);

    // Upsert this week's snapshot (dedupe on weekKey)
    const filtered = history.filter((h) => h.week !== weekKey);
    filtered.push({ week: weekKey, score, ts: new Date().toISOString() });
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(filtered.slice(-8)));
    } catch (_) { /* silent */ }
  }, [score, stats]);
  // Half-circle sweep along the TOP arc: 180° (left/SAFE) → 270° (up/moderate) → 360° (right/HIGH).
  // We drive the needle via CSS `transform: rotate()` on a <g> pivoted at (100,100)
  // — SVG doesn't reliably transition geometric attributes (x1/y1/x2/y2), so a
  // rotation transform is the only way to get a truly seamless, precise sweep.
  const angle = 180 + (score / 100) * 180;
  // Base needle points straight UP from (100,100); rotation delta shifts it around
  // the pivot. angle=180° (score=0, SAFE) resolves to rotate(-90°) → points LEFT.
  // angle=270° (score=50) → rotate(0°) → points UP. angle=360° (score=100) → rotate(90°) → points RIGHT.
  const needleRotation = angle - 270;
  // Arc path (bottom-half arc)
  const arcPath = "M 15 100 A 85 85 0 0 1 185 100";
  const dashLength = 267; // arc perimeter
  const filledLength = (score / 100) * dashLength;
  // Smoothed animation curve — same easing as the arc fill for perfect sync
  const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
  const NEEDLE_DUR = "1100ms";

  // 3D parallax tilt + numeric-change ripple (fires on risk score change)
  const { bind, Ripples } = useCardTilt({ pulseKey: score });

  return (
    <div
      {...bind}
      className="glass cosmic-aura cosmic-aura-emerald cosmic-sheen rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6 fade-up relative overflow-hidden"
      data-testid="audit-risk-meter"
      style={{ ...bind.style, minHeight: 180 }}
    >
      <Ripples />
      <div className="shrink-0" style={{ width: 200, height: 120 }}>
        <svg viewBox="0 0 200 120" width="200" height="120">
          <defs>
            <linearGradient id="riskGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#10B981" />
              <stop offset="0.5" stopColor="#F59E0B" />
              <stop offset="1" stopColor="#EF4444" />
            </linearGradient>
          </defs>
          {/* Track */}
          <path d={arcPath} stroke="rgba(255,255,255,0.06)" strokeWidth="14" fill="none" strokeLinecap="round" />
          {/* Filled portion */}
          <path
            d={arcPath}
            stroke="url(#riskGrad)"
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${filledLength} ${dashLength}`}
            style={{ transition: `stroke-dasharray ${NEEDLE_DUR} ${EASE}` }}
          />
          {/* Needle — rendered inside a rotating <g> so CSS transform animates
              cleanly (SVG geometric attrs like x1/y1/x2/y2 don't transition).
              Base pose points straight UP; we rotate around the pivot (100,100). */}
          <g
            data-testid="audit-risk-needle"
            style={{
              transform: `rotate(${needleRotation}deg)`,
              transformOrigin: "100px 100px",
              transformBox: "view-box",
              transition: `transform ${NEEDLE_DUR} ${EASE}`,
              willChange: "transform",
            }}
          >
            {/* Soft glow underlay — same hue as the risk band, tapered thick→thin */}
            <line
              x1="100" y1="100" x2="100" y2="20"
              stroke={strokeColor} strokeWidth="6"
              strokeLinecap="round"
              opacity="0.35"
              style={{ filter: `drop-shadow(0 0 6px ${strokeColor})` }}
            />
            {/* Crisp white needle */}
            <line
              x1="100" y1="100" x2="100" y2="20"
              stroke="#ffffff" strokeWidth="2.5"
              strokeLinecap="round"
            />
            {/* Counter-weight tail — makes the sweep read as physical motion */}
            <line
              x1="100" y1="100" x2="100" y2="118"
              stroke="rgba(255,255,255,0.55)"
              strokeWidth="3"
              strokeLinecap="round"
            />
            {/* Tip highlight dot */}
            <circle cx="100" cy="20" r="2.2" fill="#ffffff"
                    style={{ filter: `drop-shadow(0 0 4px ${strokeColor})` }} />
          </g>
          {/* Pivot bezel — stays stationary regardless of needle rotation */}
          <circle cx="100" cy="100" r="9" fill="#0F172A" stroke={strokeColor} strokeWidth="2"
                  style={{ transition: `stroke ${NEEDLE_DUR} ${EASE}`,
                           filter: `drop-shadow(0 0 6px ${strokeColor})` }} />
          <circle cx="100" cy="100" r="3" fill={strokeColor}
                  style={{ transition: `fill ${NEEDLE_DUR} ${EASE}` }} />
          {/* Labels */}
          <text x="15" y="118" fontSize="8" fill="#64748b" fontFamily="SF Mono, Menlo, monospace" letterSpacing="1">SAFE</text>
          <text x="180" y="118" fontSize="8" fill="#64748b" fontFamily="SF Mono, Menlo, monospace" textAnchor="end" letterSpacing="1">HIGH</text>
        </svg>
      </div>
      <div className="flex-1 min-w-0 text-center sm:text-left">
        <div className="text-slate-400 font-mono text-[10px] uppercase tracking-[0.3em] flex items-center gap-2 sm:justify-start justify-center">
          <ShieldCheck size={11} /> Audit Risk Meter
        </div>
        <div className="mt-2 flex items-baseline flex-wrap gap-3 sm:justify-start justify-center">
          <span className="font-mono text-4xl font-bold" style={{ color: strokeColor }} data-testid="audit-risk-label">
            {label}
          </span>
          <span className="text-slate-500 font-mono text-sm">index {score}/100</span>
          {delta !== null && (
            (() => {
              // Trending UP in RISK = bad (rose). Trending DOWN in risk = good (emerald).
              const flat = delta === 0;
              const chipColor = flat ? "#94a3b8" : delta > 0 ? "#f43f5e" : "#10B981";
              const bg = flat ? "rgba(148,163,184,0.12)" : delta > 0 ? "rgba(244,63,94,0.12)" : "rgba(16,185,129,0.12)";
              const arrow = flat ? "→" : delta > 0 ? "▲" : "▼";
              return (
                <span
                  data-testid="risk-delta-chip"
                  data-delta={delta}
                  title={`Change in composite audit risk vs your last recorded week`}
                  className="inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{
                    color: chipColor,
                    background: bg,
                    border: `1px solid ${chipColor}55`,
                    boxShadow: flat ? "none" : `0 0 12px -2px ${chipColor}66`,
                  }}
                >
                  <span aria-hidden="true">{arrow}</span>
                  {flat ? "flat" : `${delta > 0 ? "+" : ""}${delta}`}
                  <span className="text-slate-500">vs last wk</span>
                </span>
              );
            })()
          )}
        </div>
        <div className="text-slate-400 text-sm mt-2 max-w-md">
          {label === "Safe" && "Your books look clean. Category diversity and rounding patterns are within IRS norms."}
          {label === "Moderate" && "Watch category concentration and round-number bias. Add supporting receipts where possible."}
          {label === "High" && "Elevated flags detected. Diversify categories, avoid round numbers, and keep detailed contemporaneous notes."}
        </div>
        {(label === "High" || label === "Moderate") && (
          <button
            onClick={async () => {
              if (leadState !== "idle") return;
              setLeadState("submitting");
              try {
                await api.post("/leads/cpa-consult", {
                  risk_score: score, risk_label: label, source: "audit_risk_meter",
                });
                setLeadState("booked");
                toast.success("Reserved — a partner CPA will reach out within 24h.");
                const params = new URLSearchParams({
                  ref: "receiptsheets",
                  user_id: user?.user_id || user?.id || "",
                  risk: label,
                });
                const schedulingUrl = `https://calendly.com/receiptsheets-partners?${params.toString()}`;
                window.open(schedulingUrl, "_blank", "noopener,noreferrer");
              } catch {
                setLeadState("idle");
                toast.error("Couldn't reserve — please retry.");
              }
            }}
            disabled={leadState !== "idle"}
            data-testid="cpa-consult-cta"
            className={`mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-xl font-semibold text-sm tracking-tight transition-all disabled:cursor-not-allowed ${
              leadState === "booked"
                ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                : label === "High"
                ? "bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-lg shadow-amber-500/25"
                : "bg-white/10 hover:bg-white/15 text-white border border-emerald-500/30"
            }`}
          >
            {leadState === "submitting" && <span className="w-3 h-3 rounded-full border-2 border-slate-950/50 border-t-transparent animate-spin" />}
            {leadState === "booked"
              ? <><Check size={14} /> Consult reserved</>
              : <><CalendarCheck size={14} /> Schedule 15-min CPA consult · Free</>}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Risk Exposure Log — audit-critical timeline card ----------
// Reads immutable telemetry records seeded by AuditRiskMeter (localStorage:
// `rs.audit.exposureLog`). Rehydrates whenever the tier changes so newly
// appended breach records appear in real time without a page refresh.
// Ships with a wide-perspective 3D parallax tilt tuned for its horizontal
// aspect ratio (low profile, soft bend) + a breathing ambient glow ring.
export function RiskExposureLog() {
  // Wide-perspective tilt: smaller maxTilt for a subtle bend across the
  // long banner surface, deeper perspective so the parallax reads without
  // clipping. No numeric-change pulse ripples (log rows already animate).
  const { bind: tiltBind } = useCardTilt({ maxTilt: 3, perspective: 1400 });

  // CPA Compliance Audit Packet exporter — client-side dual CSV + PDF dump.
  // The Risk Exposure Log lives entirely in localStorage (no PII leaves the
  // browser); this handler serializes the entries and triggers *two* file
  // downloads in one click so the operator can attach both to their CPA email.
  const handleCpaExport = async () => {
    const PACKET_TITLE = "Receipt Sheets LLC Corporate Compliance Log: Audit Exposure Summary Packet for Registered S-Corporation Operations";
    const generatedAt = new Date();
    const stamp = generatedAt.toISOString().replace(/[:.]/g, "-");
    const rows = entries.map((e) => ({
      timestamp_iso: e.ts,
      composite_risk_score: e.score,
      tier: e.label || "High",
      deductible_ratio_pct: (Number(e.breach?.deductible_ratio || 0) * 100).toFixed(3),
      top_category_share_pct: (Number(e.breach?.top_category_share || 0) * 100).toFixed(3),
      round_number_ratio_pct: (Number(e.breach?.round_number_ratio || 0) * 100).toFixed(3),
      receipt_count: e.breach?.receipt_count ?? 0,
    }));

    try {
      // ---- CSV serialization ----
      const csvLines = [];
      csvLines.push(`"${PACKET_TITLE.replace(/"/g, '""')}"`);
      csvLines.push(`"Generated ${generatedAt.toUTCString()}"`);
      csvLines.push(`"Total critical-tier exposures logged: ${rows.length}"`);
      csvLines.push("");
      const cols = ["timestamp_iso", "composite_risk_score", "tier",
        "deductible_ratio_pct", "top_category_share_pct",
        "round_number_ratio_pct", "receipt_count"];
      csvLines.push(cols.join(","));
      rows.forEach((r) => {
        csvLines.push(cols.map((c) => {
          const v = String(r[c] ?? "");
          return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
        }).join(","));
      });
      const csvBlob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8" });
      const csvUrl = URL.createObjectURL(csvBlob);
      const csvA = document.createElement("a");
      csvA.href = csvUrl;
      csvA.download = `cpa-audit-log-${stamp}.csv`;
      csvA.click();
      setTimeout(() => URL.revokeObjectURL(csvUrl), 4000);

      // ---- PDF serialization (jspdf + jspdf-autotable) ----
      const { default: jsPDF } = await import("jspdf");
      const autoTableMod = await import("jspdf-autotable");
      const autoTable = autoTableMod.default || autoTableMod;
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      // Header
      doc.setFillColor(3, 7, 18);
      doc.rect(0, 0, pageW, 60, "F");
      doc.setTextColor(232, 249, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(PACKET_TITLE, 32, 26, { maxWidth: pageW - 64 });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(148, 197, 255);
      doc.text(`Generated ${generatedAt.toUTCString()}   ·   ${rows.length} critical exposure record${rows.length === 1 ? "" : "s"}`, 32, 46);
      // Body table
      autoTable(doc, {
        startY: 80,
        head: [["Timestamp (UTC)", "Score", "Tier", "Deductible %", "Top-Cat %", "Round-# %", "Receipts"]],
        body: rows.map((r) => [
          new Date(r.timestamp_iso).toUTCString(),
          `${r.composite_risk_score}/100`,
          r.tier,
          `${r.deductible_ratio_pct}%`,
          `${r.top_category_share_pct}%`,
          `${r.round_number_ratio_pct}%`,
          String(r.receipt_count),
        ]),
        headStyles: { fillColor: [15, 30, 54], textColor: [165, 243, 252], fontStyle: "bold" },
        bodyStyles: { textColor: [30, 41, 59], fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 32, right: 32 },
        theme: "striped",
      });
      // Footer
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text("Confidential — for CPA compliance review only. Records are immutable per Receipt Sheets audit ledger policy.", 32, pageH - 20);
      doc.save(`cpa-audit-log-${stamp}.pdf`);
      toast.success("CPA audit packet exported (CSV + PDF).", { id: "cpa-export", duration: 4000 });
    } catch (err) {
      toast.error("CPA export failed. Please retry.", { id: "cpa-export", description: String(err?.message || err) });
    }
  };
  const [entries, setEntries] = useState(() => {
    try {
      const raw = window.localStorage.getItem("rs.audit.exposureLog");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(-25).reverse() : [];
    } catch (_) { return []; }
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onTier = () => {
      try {
        const raw = window.localStorage.getItem("rs.audit.exposureLog");
        const arr = raw ? JSON.parse(raw) : [];
        setEntries(Array.isArray(arr) ? arr.slice(-25).reverse() : []);
      } catch (_) { /* silent */ }
    };
    window.addEventListener("audit-risk-tier", onTier);
    return () => window.removeEventListener("audit-risk-tier", onTier);
  }, []);

  if (!entries.length) {
    return (
      <div
        {...tiltBind}
        data-testid="risk-exposure-log"
        className="glass cosmic-aura cosmic-aura-emerald cosmic-sheen rounded-2xl p-4 mt-4 border border-white/5 relative"
        style={{ ...tiltBind.style }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500 flex items-center gap-1.5">
            <AlertTriangle size={11} /> Risk Exposure Log
          </div>
          <button
            type="button"
            disabled
            data-testid="cpa-audit-export-btn"
            title="No records to export yet"
            className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-lg glass-strong border border-white/10 text-slate-500 opacity-50 cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <Download size={11} /> Export CPA Audit Log
          </button>
        </div>
        <div className="text-[11px] text-slate-500 font-mono mt-2">
          No critical exposures recorded. Records auto-append on any transition into the High tier.
        </div>
      </div>
    );
  }
  return (
    <div
      {...tiltBind}
      data-testid="risk-exposure-log"
      className="glass cosmic-aura cosmic-aura-emerald cosmic-sheen rounded-2xl p-4 mt-4 relative"
      style={{ ...tiltBind.style, borderColor: "rgba(239,68,68,0.25)" }}
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-rose-300 flex items-center gap-1.5">
          <AlertTriangle size={11} /> Risk Exposure Log
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="risk-log-count"
            className="text-[10px] font-mono uppercase tracking-widest text-slate-500"
          >
            {entries.length} record{entries.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={handleCpaExport}
            data-testid="cpa-audit-export-btn"
            className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-lg glass-strong border border-cyan-500/25 text-cyan-100 hover:border-cyan-400/50 hover:bg-cyan-500/10 transition-colors inline-flex items-center gap-1.5"
            title="Download CSV + PDF for your CPA"
          >
            <Download size={11} /> Export CPA Audit Log
          </button>
        </div>
      </div>
      <ul className="divide-y divide-white/5 max-h-[220px] overflow-y-auto pr-1">
        {entries.map((e, i) => (
          <li
            key={`${e.ts}-${i}`}
            data-testid="risk-log-entry"
            className="py-2 flex items-start gap-3 text-[11px] font-mono"
          >
            <span className="text-rose-300 shrink-0 w-14">{e.score}/100</span>
            <div className="flex-1 min-w-0">
              <div className="text-slate-300">
                Ded {(e.breach?.deductible_ratio * 100).toFixed(1)}% · Top-cat {(e.breach?.top_category_share * 100).toFixed(1)}% · Round-# {(e.breach?.round_number_ratio * 100).toFixed(1)}%
              </div>
              <div className="text-slate-500 text-[10px] mt-0.5">
                {new Date(e.ts).toLocaleString()} · {e.breach?.receipt_count} receipts on record
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


const CAT_LABELS = {
  meals: "Meals & Entertainment",
  office_supplies: "Office Supplies",
  electronics: "Electronics",
  transportation: "Transportation",
  groceries: "Groceries",
  software: "Software",
  travel: "Travel",
  entertainment: "Entertainment",
  other: "Other",
};
